import { useCIQMutation, useCIQQuery } from "hooks/ciq-gql-hooks";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { ProjectContext } from "context/ProjectProvider";
import { gql, useMutation } from "@apollo/client";
import { MUTATION_SAVE_COLUMNS } from "services/graphQL/mutations";
import { getLoggedinUserId } from "services/auth";
import {
  EntityMilestoneAndDurationColumns,
  getMaxNumberOfMilestone
} from "entity-app/shared-components/entity-milestones-duration-columns";
import { AgGridReact } from "ag-grid-react";
import { message } from "utils/ciq-message";
import { UtilColumnConfig } from "components/column-config-views/column-config-util";
import {
  InfoFeatureType,
  TAuditChangeLog
} from "change-events/change-event-polling";
import { FeatureTypes } from "entity-app/constants";
import { DELETE_FEATURE_INSTANCE } from "entity-app/graphQL/ciq-feature-mutations";
import { useStatusMilestoneNameOptions } from "hooks/use-status-milestone-name-options";
import {
  BID_PACKAGE_LIST_VIEW,
  QUERY_GET_BID_PACKAGE_COLUMN_CONFIG
} from "../queries";
import { TBidPackageLog, TWorkflowOverrideReason } from "../model";
import { joinEarlyTenderReason } from "./early-tender-reason";

export const usePreconstrutionData = (props: {
  gridRef: React.RefObject<AgGridReact<any>>;
  getConditionalsFormatting?: () => Array<any>;
}) => {
  const { gridRef, getConditionalsFormatting } = props;
  const {
    gqlClientForProject,
    eventLogs,
    columnHeaders: { bidPackageHeaderMap },
    isProjectArchived
  } = useContext(ProjectContext);

  const { data: bidPackageListData, refetch: refetchBidPackageList } =
    useCIQQuery<{
      bid_package_list_func: Array<TBidPackageLog>;
      feature_workflow_instance: Array<TWorkflowOverrideReason>;
    }>(BID_PACKAGE_LIST_VIEW, {
      variables: {},
      client: gqlClientForProject,
      skip: !gqlClientForProject
    });

  const bidPackageList = useMemo(
    () => joinEarlyTenderReason(bidPackageListData),
    [bidPackageListData]
  );

  // EntityMilestoneAndDurationColumns only uses the list to derive the max
  // milestone count. Memoizing on the count (a primitive) — instead of the
  // bidPackageList reference, which is new on every refetch — keeps the
  // resulting columnDefs reference stable across inline edits. Otherwise
  // ag-grid sees new columnDefs after each refetch and resets column
  // visibility back to the colDef defaults (hide: true), clobbering any
  // milestone / duration columns the user had toggled visible.
  const maxPossibleMilestones = useMemo(() => {
    const list = bidPackageList?.bid_package_list_func || [];
    return list.reduce((max: number, item: any) => {
      const milestones = item?.milestones;
      return Array.isArray(milestones) ? Math.max(max, milestones.length) : max;
    }, 0);
  }, [bidPackageList?.bid_package_list_func]);

  const milestonesColumns = useMemo(
    () =>
      EntityMilestoneAndDurationColumns(
        maxPossibleMilestones > 0
          ? [{ milestones: new Array(maxPossibleMilestones).fill({}) }]
          : []
      ),
    [maxPossibleMilestones]
  );
  const [maxNumberOfMilestone, setMaxNumberOfMilestone] = useState({
    numberOfMaxOffsets: 0,
    numberOfMaxMileStones: 0
  });

  const loggedinUserId = useMemo(() => getLoggedinUserId(), []);
  const { data: savedColumnsData } = useCIQQuery<{
    project_users: Array<{ bid_package_column_config: any }>;
  }>(QUERY_GET_BID_PACKAGE_COLUMN_CONFIG, {
    fetchPolicy: "no-cache",
    client: gqlClientForProject,
    skip: !gqlClientForProject || !loggedinUserId,
    variables: {
      where: { user_id: { _eq: loggedinUserId } }
    }
  });

  const previousEventLogs = useRef(eventLogs);

  useEffect(() => {
    if (eventLogs?.length && previousEventLogs.current !== eventLogs) {
      /**
       * If info.feature_type == "BID_PACKAGE" then on log page refresh everything
       * if any planning item changed then milestone of a few bids will might change hence need to reload the list
       * Delete events use feature_type_ids: [4] (BID_PACKAGE) with data_source feature_instance
       */
      const allImpactingFeatureTypes: any = [
        InfoFeatureType.bidPackage,
        InfoFeatureType.designPackage,
        InfoFeatureType.planningActivity,
        InfoFeatureType.planningMaterial,
        InfoFeatureType.planningSubmittal
      ];
      const conditionForBidPackage = (log: TAuditChangeLog) =>
        allImpactingFeatureTypes.includes(log?.info?.feature_type) ||
        log?.info?.feature_type_ids?.includes(FeatureTypes.BID_PACKAGE);

      if (eventLogs.some(conditionForBidPackage)) {
        refetchBidPackageList();
      }
    }
    previousEventLogs.current = eventLogs;
  }, [eventLogs, refetchBidPackageList]);

  const calculateMaxNumberOfMilestone = useCallback(() => {
    const maxMileStone = getMaxNumberOfMilestone({
      api: gridRef.current?.api
    });
    if (!maxMileStone) return;

    setMaxNumberOfMilestone(maxMileStone);
  }, [gridRef]);

  const savedColumns = useMemo(() => {
    if (!savedColumnsData || !savedColumnsData?.project_users?.length)
      return null;

    const resArr = savedColumnsData?.project_users;
    const colConfig = resArr[resArr.length - 1];
    calculateMaxNumberOfMilestone();
    return colConfig;
  }, [calculateMaxNumberOfMilestone, savedColumnsData]);

  const [saveColumnsStateMutation] = useMutation<any>(MUTATION_SAVE_COLUMNS, {
    client: gqlClientForProject
  });

  const [mutationDeleteBidPackage] = useCIQMutation(
    gql(DELETE_FEATURE_INSTANCE),
    {
      client: gqlClientForProject
    }
  );

  const statusMilestoneOptions = useStatusMilestoneNameOptions(
    FeatureTypes.BID_PACKAGE
  );

  const onSaveColumnState = async () => {
    if (isProjectArchived || !gridRef.current) return;
    const columnsStates = UtilColumnConfig.getColumnStateFromGrid({
      gridRef: {
        api: gridRef.current!.api
      },
      conditionals_formatting: getConditionalsFormatting?.() ?? []
    });

    try {
      const variables = {
        bid_package_column_config: columnsStates
      };
      const updateResponse: any = await saveColumnsStateMutation({
        variables
      });
      if (updateResponse.errors) {
        message.error({ content: updateResponse.errors[0].message });
      } else {
        // message.success("Updated successfully");
      }
    } catch {
      console.error("An error occured");
    }
  };

  return {
    bidPackageList,
    refetchBidPackageList,
    milestonesColumns,
    maxNumberOfMilestone,
    calculateMaxNumberOfMilestone,
    savedColumns,
    onSaveColumnState,
    bidPackageHeaderMap,
    mutationDeleteBidPackage,
    statusMilestoneOptions
  };
};
